import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useVaultStore } from '../store/vaultStore';
import { useI18n } from '../i18n';
import { showToast } from '../utils/toast';

/**
 * Watches vault for master key conflict (two devices with created_here) and
 * also for a conflict resolution that makes us the loser.
 * Shows a toast and lets the user navigate to the conflict resolution page.
 */
export function useMkConflictWatcher() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const conflictShownRef = useRef(false);
  const loserShownRef = useRef(false);

  const masterKey = useAuthStore((state) => state.masterKey);
  const currentDeviceId = useAuthStore((state) => state.currentDeviceId);

  const hasConflict = useVaultStore((state) => {
    if (!currentDeviceId) return false;
    const devices = state.vaultData?.devices ?? {};
    const createdHere = Object.entries(devices).filter(
      ([, entry]) => entry.masterKeyStatus === 'created_here'
    );
    // Conflict = this device is created_here AND at least one other device is too.
    const weAreCreatedHere = devices[currentDeviceId]?.masterKeyStatus === 'created_here';
    return weAreCreatedHere && createdHere.length >= 2;
  });

  const weAreLoser = useVaultStore((state) => {
    if (!currentDeviceId || !masterKey) return false;
    const res = state.vaultData?.mkConflictResolution;
    // We are loser if another device declared itself winner and we still have a key.
    return !!(res && res.winnerDeviceId !== currentDeviceId);
  });

  useEffect(() => {
    if (hasConflict && !conflictShownRef.current) {
      conflictShownRef.current = true;
      showToast({
        message: t('mkConflict.title'),
        variant: 'warning',
        durationMs: 15000,
        actionLabel: t('common.resolve'),
        onAction: () => navigate('/master-key/conflict')
      });
    }
  }, [hasConflict, navigate, t]);

  useEffect(() => {
    if (weAreLoser && !loserShownRef.current) {
      loserShownRef.current = true;
      showToast({
        message: t('mkConflict.loserNotice'),
        variant: 'warning',
        durationMs: 15000,
        actionLabel: t('common.resolve'),
        onAction: () => navigate('/master-key/conflict')
      });
    }
  }, [navigate, t, weAreLoser]);
}
